import { EPopupSubType } from 'constants/enums';
import { FormikErrors } from 'formik';
import { FILTER_TYPE_SEPARATOR, VALIDATION_TOAST_DESCRIPTIONS } from 'views/OfferDesign/OfferDesign.consts';
import {
  FilterCriteria,
  GradientColor,
  OfferDesignFormValues,
  OfferDesignRow,
  OfferDesignTypes,
  SaveValidationCheck,
  UniqueFlag
} from 'views/OfferDesign/OfferDesign.types';

import { GradientDirection } from 'components/AcGradientInput/AcGraidentInput.types';

export const resolveOfferDesignType = (
  offerUiType: string,
  offerUiSubType?: string
): OfferDesignTypes => {
  if (offerUiType !== OfferDesignTypes.PopUp || !offerUiSubType) {
    return offerUiType as OfferDesignTypes;
  }

  if (offerUiSubType === EPopupSubType.REWARD_CALENDAR) {
    return OfferDesignTypes.RewardCalendar;
  }

  if (offerUiSubType === EPopupSubType.DAILY_BONUS) {
    return OfferDesignTypes.PopUp;
  }

  return OfferDesignTypes.TriggeredPopup;
};

export const getParam = (params: URLSearchParams, key: string): string =>
  params.get(key) || '';

export const getArrayParam = (
  params: URLSearchParams,
  key: string
): string[] => {
  const value = params.get(key);
  return value ? value.split(FILTER_TYPE_SEPARATOR) : [];
};

export const toggleArrayValue = (current: string[], value: string): string[] =>
  current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];

const includesCaseInsensitive = (
  value: string | undefined,
  query: string
): boolean => !!value?.toLowerCase().includes(query.toLowerCase());

const matchesExternalId = (offer: OfferDesignRow, query: string): boolean =>
  !query || includesCaseInsensitive(offer.externalId, query);

const matchesName = (offer: OfferDesignRow, query: string): boolean =>
  !query || includesCaseInsensitive(offer.name, query);

const matchesType = (offer: OfferDesignRow, types: string[]): boolean =>
  types.length === 0 || types.includes(resolveOfferDesignType(offer.offerUiType || '', offer.offerUiSubType));

const matchesOffer = (
  offer: OfferDesignRow,
  { externalId, name, types }: FilterCriteria
): boolean =>
  matchesExternalId(offer, externalId) &&
  matchesName(offer, name) &&
  matchesType(offer, types);

export const filterOffers = (
  offers: OfferDesignRow[],
  criteria: FilterCriteria
): OfferDesignRow[] => offers.filter((offer) => matchesOffer(offer, criteria));

export const normalizeBgColor = (backgroundColor: GradientColor | string | undefined): GradientColor | undefined => {
  if (!backgroundColor) return undefined;
  if (typeof backgroundColor === 'string') return { colorOne: backgroundColor };
  return backgroundColor;
};

export const sanitizeExternalId = (value?: string) => {
  if (!value) return '';
  return value.trim().replace(/\s+/g, '_');
};

export const formatName = (name?: string, isDup?: boolean) => {
  if (!name) return '';
  return isDup ? `${name}_copy` : name;
};

export const getFieldError = (
  fieldName: string,
  touched: Record<string, boolean | undefined>,
  errors: Record<string, string | undefined>,
  uniqueFlags: Record<string, boolean>,
  uniqueFlag?: UniqueFlag
): string | undefined => {
  const validationError = touched[fieldName] && errors[fieldName];
  const isUniquenessViolation = uniqueFlag && !uniqueFlags[uniqueFlag];
  const fieldLabel = fieldName === 'name' ? 'Name' : 'External ID';

  const uniqueError = isUniquenessViolation
    ? `${fieldLabel} already exists`
    : '';
  return (validationError as string) || uniqueError || undefined;
};

export const getInputErrorProps = (error: string | undefined) => ({
  state: (error ? 'error' : 'default') as 'error' | 'default',
  inputClassName: error ? 'border-base-destructive' : '',
  description: error
});

export const getFirstSaveError = (
  validationErrors: FormikErrors<OfferDesignFormValues>,
  checks: SaveValidationCheck[],
  fieldOrder?: string[]
): { message: string; description: string } | null => {
  if (fieldOrder) {
    for (const field of fieldOrder) {
      const failedCheck = checks.find((c) => c.fieldKey === field && c.failed);
      if (failedCheck) {
        return { message: failedCheck.message, description: failedCheck.description };
      }

      const yupError = validationErrors[field as keyof OfferDesignFormValues];
      if (yupError) {
        return {
          message: yupError as string,
          description: VALIDATION_TOAST_DESCRIPTIONS[field] || 'Please fill in the required field to continue',
        };
      }
    }

    const nonFieldCheck = checks.find((c) => !c.fieldKey && c.failed);
    return nonFieldCheck
      ? { message: nonFieldCheck.message, description: nonFieldCheck.description }
      : null;
  }

  const [firstYupEntry] = Object.entries(validationErrors);
  if (firstYupEntry) {
    const [fieldKey, errorMessage] = firstYupEntry;
    return {
      message: errorMessage as string,
      description: VALIDATION_TOAST_DESCRIPTIONS[fieldKey] || 'Please fill in the required field to continue',
    };
  }

  const firstFailedCheck = checks.find((check) => check.failed);
  return firstFailedCheck
    ? { message: firstFailedCheck.message, description: firstFailedCheck.description }
    : null;
};

export const getFontSizeError = (fontSize: number, min = 8, max = 20) => {
  if (fontSize < min) return `Size can't be lower than ${min}`;
  if (fontSize > max) return `Size can't be greater than ${max}`;
  return undefined;
};

export const handleButtonSuffixUpload = (
  setFieldValue: (field: string, value: unknown) => void,
  url: string,
  name: string,
  mimeType: string
) => {
  const isAnimation = name?.endsWith('.json') || mimeType?.includes('json');

  if (isAnimation) {
    setFieldValue('buttonSuffixAnimation', url);
    setFieldValue('buttonSuffixImage', '');
  } else {
    setFieldValue('buttonSuffixImage', url);
    setFieldValue('buttonSuffixAnimation', '');
  }
};

const isGradientColor = (value: unknown): value is GradientColor =>
  typeof value === 'object' && value !== null && 'colorOne' in value;

export const normalizeGradientColor = (
  value: unknown,
  fallback: GradientColor
): GradientColor => {
  if (isGradientColor(value)) return value;
  return fallback;
};

export const toNumberWithFallback = (value: unknown, fallback: number): number => {
  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
};

export const buildGradientValue = (
  colorOne: string,
  colorTwo?: string,
  gradientDirection?: GradientDirection
): GradientColor => ({
  colorOne,
  ...(colorTwo && { colorTwo }),
  ...(gradientDirection && { gradientDirection })
});
